import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { Tag, UserBookmarkedTag } from '@/lib/pocketbase'

// Fetch all tags
export function useTags(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 50 } = filters || {}

  return useQuery({
    queryKey: ['tags', filters],
    queryFn: async () => {
      let filter = ''
      if (search) {
        filter = `(name_th ~ "${search}" || name_en ~ "${search}" || slug ~ "${search}")`
      }

      const result = await pb.collection('tags').getList<Tag>(page, perPage, {
        filter,
      })
      return result
    },
  })
}

// Fetch single tag
export function useTag(tagId: string) {
  return useQuery({
    queryKey: ['tag', tagId],
    queryFn: async () => {
      const tag = await pb.collection('tags').getOne<Tag>(tagId)
      return tag
    },
    enabled: !!tagId,
  })
}

// Fetch user's bookmarked tags
export function useBookmarkedTags() {
  return useQuery({
    queryKey: ['bookmarked-tags'],
    queryFn: async () => {
      const userId = pb.authStore.model?.id
      if (!userId) return []

      const result = await pb.collection('user_bookmarked_tags').getList<UserBookmarkedTag>(1, 100, {
        filter: `user = "${userId}"`,
        expand: 'tag',
      })

      return result.items
        .map((item) => {
          const tagExpand = (item.expand as any)?.tag
          return tagExpand
            ? ({
                ...tagExpand,
                name:
                  tagExpand.name ||
                  tagExpand.name_th ||
                  tagExpand.name_en ||
                  '',
              } as Tag)
            : undefined
        })
        .filter((tag): tag is Tag => tag !== undefined)
    },
  })
}

// Fetch tenders by tag
export function useTendersByTag(tagId: string, page = 1) {
  return useQuery({
    queryKey: ['tenders-by-tag', tagId, page],
    queryFn: async () => {
      const result = await pb.collection('tenders').getList(page, 20, {
        filter: `tags ~ "${tagId}"`,
      })
      return result
    },
    enabled: !!tagId,
  })
}
